import HlsBlob from "./hls_blob";
import HlsEncryption from "./hls_encryption";
import type { IHlsData, IHlsPlaylistData, IHlsPreparedThumbnail, IHlsThumbnail } from "./hls_models";

class HlsParser {
  blob = new HlsBlob();
  hlsEncryption = new HlsEncryption();

  parseToBlobUrl(data: IHlsData): string {
    const encKeyUrl = this.blob.generateBlobLink(this.hlsEncryption.encrypt(data.enc));

    const videoPlayLists = this.parsePlayLists(data.video_playlists, data, encKeyUrl);

    const audioPlaylist = this.parsePlayLists(data.audio_playlists, data, encKeyUrl);

    data.master = this.updateHlsVideoPlaylist(data.master, videoPlayLists);

    data.master = this.updateHlsAudioPlaylist(data.master, audioPlaylist);

    return this.blob.generateBlobLink(this.hlsEncryption.encrypt(data.master));
  }

  parseThumbnails(baseUrl: string, thumbs: IHlsThumbnail): IHlsPreparedThumbnail {
    return {
      large: this.processVtt(baseUrl, thumbs.large),
      medium: this.processVtt(baseUrl, thumbs.medium),
    };
  }

  processVtt(baseUrl: string, playlist: IHlsPlaylistData): string {
    if (!playlist?.data) return "";

    return playlist.data.replace(/(sprite-\d+\.jpg)/g, (match) => {
      return `${baseUrl}/${playlist.base_url}/${match}`;
    });
  }

  parsePlayLists(playlists: IHlsPlaylistData[], data: IHlsData, encUrl: string): IHlsPlaylistData[] {
    const updated = playlists.map((item) => {
      return this.updatePlayList({
        baseUrl: data.base_url,
        encUrl: encUrl,
        playlistData: item,
        token: data.token,
      });
    });

    return updated;
  }

  updatePlayList({
    baseUrl,
    token,
    encUrl,
    playlistData,
  }: {
    baseUrl: string;
    encUrl: string;
    token?: string | null;
    playlistData: IHlsPlaylistData;
  }): IHlsPlaylistData {
    playlistData.data = playlistData.data.replace(
      /#EXT-X-KEY:METHOD=AES-128,URI="([^"]+)"/,
      `#EXT-X-KEY:METHOD=AES-128,URI="${encUrl}"`,
    );

    playlistData.data = playlistData.data.replace(/(stream-\d+\.ts)/g, (match) => {
      return `${baseUrl}/${playlistData.base_url}/${match}?t=${token}`;
    });

    return playlistData;
  }

  updateHlsAudioPlaylist(masterPlaylist: string, audioList: IHlsPlaylistData[]): string {
    const uriToBlobMap = new Map<string, string>();

    audioList.forEach((audio) => {
      const url = this.blob.generateBlobLink(this.hlsEncryption.encrypt(audio.data));

      uriToBlobMap.set(audio.uri, url);
    });

    const updatedPlaylist = masterPlaylist
      .split("\n")
      .map((line) => {
        if (line.startsWith("#EXT-X-MEDIA") && line.includes("URI=")) {
          return line.replace(/URI="([^"]+)"/, (_, uri) => {
            return `URI="${uriToBlobMap.get(uri) || uri}"`;
          });
        }
        return line;
      })
      .join("\n");

    return updatedPlaylist;
  }

  updateHlsVideoPlaylist(masterPlaylist: string, videoList: IHlsPlaylistData[]): string {
    const uriToBlobMap = new Map<string, string>();

    videoList.forEach((video) => {
      const url = this.blob.generateBlobLink(this.hlsEncryption.encrypt(video.data));

      uriToBlobMap.set(video.uri, url);
    });

    const updatedPlaylist = masterPlaylist
      .split("\n")
      .map((line) => {
        if (line.startsWith("#EXT-X-STREAM-INF") && !line.includes("AUDIO=")) {
          return line;
        } else if (line.startsWith("video/")) {
          return uriToBlobMap.get(line) || line;
        }
        return line;
      })
      .join("\n");

    return updatedPlaylist;
  }
}

export default HlsParser;
